import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { BedtimeOutlined } from '@mui/icons-material';
import { CLIENT_WEEKLY_ROUTINE } from 'api/graphql/playCoach';
import { DAY_VALUES, dayLabel, muscleLabel } from 'components/play/constants';

/**
 * OTRIX Play (coach) — semana de la rutina del cliente en SOLO lectura: qué tiene
 * planificado cada día (o descanso). El coach ve el plan; no lo edita desde aquí.
 */
function PlayClientRoutineView({ clientId }) {
  const { t } = useTranslation(['play']);
  const { data, loading } = useQuery(CLIENT_WEEKLY_ROUTINE, {
    variables: { user_id: clientId },
    fetchPolicy: 'cache-and-network'
  });

  const routine = data?.clientWeeklyRoutine || null;

  const byDay = useMemo(() => {
    const map = {};
    (routine?.items || [])
      .filter((item) => item.exercise)
      .forEach((item) => {
        (map[item.day_of_week] = map[item.day_of_week] || []).push(item);
      });
    Object.keys(map).forEach((d) => map[d].sort((a, b) => (a.position || 0) - (b.position || 0)));
    return map;
  }, [routine]);

  if (loading && !routine) {
    return (
      <Stack spacing={1.25}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: '14px' }} />
        ))}
      </Stack>
    );
  }

  if (!routine) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', border: '1px dashed', borderColor: 'hairline.main', borderRadius: 3, color: 'text.secondary' }}>
        <Typography variant="body2" fontWeight={600}>
          {t('coach.routineEmpty', { ns: 'play', defaultValue: 'Este cliente aún no tiene una rutina activa.' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.25}>
      {DAY_VALUES.map((day) => {
        const items = byDay[day] || [];
        const isRest = items.length === 0;
        return (
          <Stack
            key={day}
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              p: 1.5,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: isRest ? 'surface.overlay' : 'surface.content'
            }}>
            <Typography
              variant="caption"
              fontWeight={800}
              sx={{ width: 76, flex: 'none', letterSpacing: '0.06em', textTransform: 'uppercase', color: isRest ? 'text.disabled' : 'gold.main' }}>
              {dayLabel(t, day)}
            </Typography>
            {isRest ? (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.disabled' }}>
                <BedtimeOutlined sx={{ fontSize: 15 }} />
                <Typography variant="caption" fontWeight={600}>
                  {t('home.week.rest', { ns: 'play' })}
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
                {items.map((item) => (
                  <Chip
                    key={item.id}
                    size="small"
                    label={`${item.exercise.localized_name || item.exercise.name} · ${item.target_sets || '?'}×${item.target_reps || '?'}`}
                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'surface.raised', border: '1px solid', borderColor: 'hairline.main' }}
                  />
                ))}
                <Chip
                  size="small"
                  label={muscleLabel(t, items[0]?.exercise?.muscle_group)}
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'gold.soft', color: 'gold.main' }}
                />
              </Stack>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

PlayClientRoutineView.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default PlayClientRoutineView;
